import React from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

/**
 * @constant
 * @description Custom CSS styles for the password text field.
 * Defines the focused border color to match the application theme.
 *
 * @type {string}
 */
const customStyle = `
.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
  border-color: #F5E0B5;
}
`;

/**
 * @component
 * @description Specialized password input field component with visibility toggle functionality.
 * Built on Material-UI TextField with enhanced security features and user experience improvements.
 * Provides password visibility toggle, custom styling, and validation support.
 *
 * Key Features:
 * - Password visibility toggle with eye icon
 * - Custom theme-consistent styling
 * - Built-in validation error display
 * - Security-focused auto-complete disabled
 * - Responsive design
 * - Helper text support for validation messages
 * - Accessible keyboard navigation
 * - Icon button for password reveal/hide
 * - Custom placeholder support
 * - Mouse down event handling for security
 *
 * @param {Object} props - Component props
 * @param {boolean} props.showPassword - Whether password is currently visible
 * @param {Function} props.handleClickShowPassword - Handler for password visibility toggle
 * @param {Function} props.handleMouseDownPassword - Handler for mouse down events on visibility button
 * @param {Function} props.onChange - Change event handler for input value
 * @param {boolean} [props.error=false] - Whether the field has validation errors
 * @param {string|number} props.value - Current password value
 * @param {string} [props.helperText] - Helper text to display below the field
 * @param {string} props.name - Input field name attribute
 * @param {Object} [props.props] - Additional props including placeholder
 * @param {string} [props.props.placeholder] - Placeholder text for the input
 * @returns {JSX.Element} Password input field with visibility toggle
 *
 * @example
 * // Basic password field with visibility toggle
 * const [showPassword, setShowPassword] = useState(false);
 *
 * <PasswordTextField
 *   name="password"
 *   value={formValues.password}
 *   onChange={handleChange}
 *   showPassword={showPassword}
 *   handleClickShowPassword={() => setShowPassword(!showPassword)}
 *   handleMouseDownPassword={(e) => e.preventDefault()}
 *   props={{ placeholder: "Enter password" }}
 * />
 *
 * @example
 * // Password field with validation
 * <PasswordTextField
 *   name="password"
 *   value={formValues.password}
 *   onChange={handleChange}
 *   showPassword={showPassword}
 *   handleClickShowPassword={togglePasswordVisibility}
 *   handleMouseDownPassword={handleMouseDown}
 *   error={!!errors.password}
 *   helperText={errors.password}
 *   props={{ placeholder: "Enter your password" }}
 * />
 *
 * @example
 * // Complete password field implementation
 * function LoginForm() {
 *   const [showPassword, setShowPassword] = useState(false);
 *   const [password, setPassword] = useState('');
 *   const [error, setError] = useState('');
 *
 *   const handleClickShowPassword = () => setShowPassword(!showPassword);
 *   const handleMouseDownPassword = (event) => event.preventDefault();
 *
 *   return (
 *     <PasswordTextField
 *       name="password"
 *       value={password}
 *       onChange={(e) => setPassword(e.target.value)}
 *       showPassword={showPassword}
 *       handleClickShowPassword={handleClickShowPassword}
 *       handleMouseDownPassword={handleMouseDownPassword}
 *       error={!!error}
 *       helperText={error}
 *       props={{ placeholder: "Enter your password" }}
 *     />
 *   );
 * }
 *
 * @see TextField - Material-UI TextField component
 * @see InputAdornment - Material-UI input adornment
 * @see IconButton - Material-UI icon button
 * @see Visibility - Material-UI visibility icon
 * @see VisibilityOff - Material-UI visibility off icon
 */
function PasswordTextField({
    showPassword,
    handleClickShowPassword,
    handleMouseDownPassword,
    onChange,
    error,
    value,
    helperText,
    name,
    props
}) {
    return (
        <div>
            <style>{customStyle}</style> {/* Include the CSS within a style tag */}
            <TextField
                size="small"
                margin="normal"
                fullWidth
                name={name}
                type={showPassword ? 'text' : 'password'}
                id={name}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                autoComplete="off"
                value={value}
                onChange={onChange}
                error={error}
                helperText={helperText}
                style={{ marginTop: '0px' }}
                FormHelperTextProps={{ style: { marginLeft: '0px' } }}
                placeholder={props?.placeholder ? props?.placeholder : ''}
            />
        </div>
    );
}

export default PasswordTextField;
